import React, { useState } from 'react';
import { Card, CardContent, Loading, ErrorMessage, Badge } from '../common';
import { Calendar, Users, DollarSign, RefreshCw, ChevronLeft, Clock, Check } from 'lucide-react';
import { Service } from '../../models/Service';

// Metrics section with individual loading state
export function MetricsSection({ metrics, loading, error, t, onRefresh }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="p-2 md:p-6 h-14 md:h-auto">
            <Loading text="" className="h-6 md:h-24" />
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <ErrorMessage
          title="שגיאה בטעינת נתונים"
          message={`לא ניתן לטעון את המדדים: ${error}`}
          compact
        />
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

return (
  <div className="relative grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-6 mt-2">
    {/* Floating refresh button */}
    <button
      onClick={onRefresh}
      disabled={loading}
      className="absolute -top-1 -left-1 z-10 w-8 h-8 md:w-10 md:h-10 bg-white/70 backdrop-blur-sm hover:bg-white/90 transition-all duration-200 rounded-full shadow-lg hover:shadow-xl flex items-center justify-center group disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 md:w-5 md:h-5 text-gray-600 group-hover:text-gray-800 transition-colors ${loading ? 'animate-spin' : ''}`} />
    </button>

    {/* This Week's Appointments */}
    <Card className="bg-white border-0 border-t-4 border-t-green-500 h-14 md:h-24">
      <CardContent className="p-0 md:p-6 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="w-8 h-8 md:w-12 md:h-12 ml-4 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
            <Users className="w-4 h-4 md:w-6 md:h-6 text-green-600" />
          </div>
          <div className="flex-1">
            <p className="text-gray-600 text-xs md:text-sm truncate">{t.weeksAppointments}</p>
            <p className="text-base md:text-3xl font-bold text-gray-900 leading-tight">
              {(metrics?.this_week?.completed_appointments || 0)}/{metrics?.this_week?.total_appointments || 0}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* This Month's Appointments */}
    <Card className="bg-white border-0 border-t-4 border-t-purple-500 h-14 md:h-24">
      <CardContent className="p-0 md:p-6 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="w-8 h-8 md:w-12 md:h-12 ml-4 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
            <Calendar className="w-4 h-4 md:w-6 md:h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <p className="text-gray-600 text-xs md:text-sm truncate">תורים החודש</p>
            <p className="text-base md:text-3xl font-bold text-gray-900 leading-tight">
              {(metrics?.this_month?.completed_appointments || 0)}/{metrics?.this_month?.total_appointments || 0}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Week Revenue */}
    <Card className="bg-white border-0 border-t-4 border-t-orange-500 h-14 md:h-24">
      <CardContent className="p-0 md:p-6 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="w-8 h-8 md:w-12 md:h-12 ml-4 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
            <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <p className="text-gray-600 text-xs md:text-sm truncate">{t.weekRevenue}</p>
            <p className="text-base md:text-3xl font-bold text-gray-900 leading-tight">₪{metrics?.this_week?.revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Month Revenue */}
    <Card className="bg-white border-0 border-t-4 border-t-blue-500 h-14 md:h-24">
      <CardContent className="p-0 md:p-6 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="w-8 h-8 md:w-12 md:h-12 ml-4 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
            <DollarSign className="w-4 h-4 md:w-6 md:h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-gray-600 text-xs md:text-sm truncate">הכנסות החודש</p>
            <p className="text-base md:text-3xl font-bold text-gray-900 leading-tight">₪{metrics?.this_month?.revenue?.toFixed(2) || '0.00'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);
}

export function TodayScheduleSection({ schedule, loading, error, onAppointmentClick, onNewAppointmentClick, onNavigate, customers, services, t }) {
  const getAppointmentDetails = (appointment) => {
    const customer = customers?.find(c => c.id === appointment.customer_id);
    const service = services?.find(s => s.id === appointment.service_id);
    return {
      customerName: customer?.name || appointment.customer_name || t.unknownCustomer,
      serviceName: service?.name || appointment.service_name || t.unknownService,
      customerPhone: customer?.phone || appointment.customer_phone || t.noPhone,
      duration: appointment.duration || service?.duration || 45
    };
  };

  // Get status color for badge
  const getStatusColor = (status) => {
    const statusMap = {
      'pending_approval': 'warning',
      'scheduled': 'primary',
      'confirmed': 'success', 
      'completed': 'success',
      'cancelled': 'danger',
      'no_show': 'warning',
      'no_show_justified': 'secondary'
    };
    return statusMap[status] || 'secondary';
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      'pending_approval': 'ממתין',
      'scheduled': 'מתוזמן',
      'confirmed': 'מאושר',
      'completed': 'הושלם',
      'cancelled': 'בוטל',
      'no_show': 'לא הגיע',
      'no_show_justified': 'לא הגיע - מוצדק'
    };
    return labels[status] || status;
  };

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name || name === t.unknownCustomer) return '?';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="lg:col-span-2">
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl">
        {/* Header inside card */}
        <div className="flex justify-between items-center p-6 pb-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 ml-2 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-lg font-bold text-gray-900">הלו"ז שלי להיום</span>
          </div>
          <button 
            onClick={() => onNavigate('schedule')}
            className="text-blue-600 hover:text-blue-700 flex items-center font-medium"
          >
            ראה הכל
            <ChevronLeft className="w-4 h-4 mr-2" />
          </button>
        </div>
        
        {/* Content */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 bg-red-50/50 rounded-xl">
              <p className="text-red-600 mb-2">שגיאה בטעינת הלו"ז</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          ) : schedule.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">אין פגישות מתוכננות להיום</p>
              <button
                onClick={onNewAppointmentClick}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:from-pink-600 hover:to-purple-700 transition-all duration-200"
              >
                קבע פגישה חדשה
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100/50 max-h-96 overflow-y-auto">
              {schedule.map((appointment) => {
                const details = getAppointmentDetails(appointment);
                const initials = getInitials(details.customerName);
                
                return (
                  <div 
                    key={appointment.id} 
                    onClick={() => onAppointmentClick(appointment)}
                    className="p-4 hover:bg-white/30 transition-all duration-200 cursor-pointer group"
                  >
                    {/* Status Badge - Top Left */}
                    <div className="text-left z-10">
                      <Badge
                        variant={getStatusColor(appointment.status)}
                        size="small"
                      >
                        {getStatusLabel(appointment.status)}
                      </Badge>
                    </div>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 ml-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-purple-700">
                            {initials}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">
                              {details.customerName}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {details.serviceName}
                          </p>
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3 ml-1" />
                            <span>{appointment.time}</span>
                            <span className="mx-2">•</span>
                            <span>{details.duration} דקות</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className="text-lg font-semibold text-gray-900">
                          ₪{appointment.price || 0}
                        </p>
                        {appointment.paymentStatus && (
                          <span className={`text-xs ${
                            appointment.paymentStatus === 'paid' 
                              ? 'text-green-600' 
                              : 'text-orange-600'
                          }`}>
                            {appointment.paymentStatus === 'paid' ? 'שולם' : 'ממתין'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function OverdueAppointmentsSection({ appointments, unpaidAppointments, loading, error, onAppointmentClick, onCompleteAppointment, onMarkAsPaid, customers, services, t }) {
  // Local state for optimistic updates
  const [completingIds, setCompletingIds] = useState(new Set());
  const [markingAsPaidIds, setMarkingAsPaidIds] = useState(new Set());
  const [removingIds, setRemovingIds] = useState(new Set());

  const getAppointmentDetails = (appointment) => {
    const customer = customers?.find(c => c.id === appointment.customer_id);
    const service = services?.find(s => s.id === appointment.service_id);
    return {
      customerName: customer?.name || appointment.customer_name || t.unknownCustomer,
      serviceName: service?.name || appointment.service_name || t.unknownService,
      customerPhone: customer?.phone || appointment.customer_phone || t.noPhone,
      duration: appointment.duration || service?.duration || 45
    };
  };

  // Get customer initials for avatar
  const getInitials = (name) => {
    if (!name || name === t.unknownCustomer) return '?';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  // Format date to display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Separate appointments into overdue (from backend) and filter out removed ones (after animation)
  // Also exclude services that don't require payment (e.g., breaks, blocked time)
  const overdueAppointments = (appointments?.filter(apt =>
    apt.status === 'scheduled' && apt.date && new Date(apt.date + ' ' + apt.time) < new Date() && !Service.getServicesWithoutPayment().includes(apt.service_name)
  ) || []).filter(apt => !removingIds.has(apt.id));

  // Unpaid appointments now come from the separate prop and filter out removed ones (after animation)
  // Also exclude services that don't require payment (e.g., breaks, blocked time)
  const unpaidList = (unpaidAppointments || []).filter(apt => !removingIds.has(apt.id) && !Service.getServicesWithoutPayment().includes(apt.service_name));

  const renderAppointmentList = (appointmentList, emptyMessage, showCompleteButton = false, showMarkAsPaidButton = false) => {
    if (appointmentList.length === 0) {
      return (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{emptyMessage}</p>
        </div>
      );
    }

    const handleComplete = async (e, appointment) => {
      e.stopPropagation();
      setCompletingIds(prev => new Set([...prev, appointment.id]));
      try {
        if (onCompleteAppointment) {
          await onCompleteAppointment(appointment);
        }
        setTimeout(() => {
          setRemovingIds(prev => new Set([...prev, appointment.id]));
          setCompletingIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(appointment.id);
            return newSet;
          });
        }, 400);
      } catch (error) {
        setCompletingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(appointment.id);
          return newSet;
        });
      }
    };

    const handleMarkAsPaidClick = async (e, appointment) => {
      e.stopPropagation();
      setMarkingAsPaidIds(prev => new Set([...prev, appointment.id]));
      try {
        if (onMarkAsPaid) {
          await onMarkAsPaid(appointment);
        }
        setTimeout(() => {
          setRemovingIds(prev => new Set([...prev, appointment.id]));
          setMarkingAsPaidIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(appointment.id);
            return newSet;
          });
        }, 400);
      } catch (error) {
        setMarkingAsPaidIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(appointment.id);
          return newSet;
        });
      }
    };

    return (
      <div className="divide-y divide-gray-100/50 max-h-80 overflow-y-auto">
        {appointmentList.map((appointment) => {
          const details = getAppointmentDetails(appointment);
          const initials = getInitials(details.customerName);

          const isCompleting = completingIds.has(appointment.id);
          const isMarkingAsPaid = markingAsPaidIds.has(appointment.id);
          const isAnimating = isCompleting || isMarkingAsPaid;

          return (
            <div
              key={appointment.id}
              className={`overflow-hidden transition-all duration-300 ease-out ${
                isAnimating ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100'
              }`}
              style={{
                transition: 'max-height 400ms ease-out, opacity 300ms ease-out, margin 300ms ease-out'
              }}
            >
              <div
                onClick={() => onAppointmentClick(appointment)}
                className={`p-4 hover:bg-white/30 transition-all duration-300 cursor-pointer group ${
                  isAnimating ? 'scale-95 -translate-x-4' : 'scale-100 translate-x-0'
                }`}
                style={{
                  pointerEvents: isAnimating ? 'none' : 'auto',
                  transition: 'transform 300ms ease-out'
                }}
              >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="w-10 h-10 ml-3 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-purple-700">
                      {initials}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">
                        {details.customerName}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {details.serviceName}
                    </p>
                    <div className="flex items-center mt-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3 ml-1" />
                      <span>{formatDate(appointment.date)}</span>
                      <span className="mx-2">•</span>
                      <Clock className="w-3 h-3 ml-1" />
                      <span>{appointment.time}</span>
                      <span className="mx-2">•</span>
                      <span>{details.duration} דקות</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-left">
                    <p className="text-lg font-semibold text-gray-900">
                      ₪{appointment.price || 0}
                    </p>
                    {(appointment.is_paid === false || appointment.is_paid === 0) && !Service.getServicesWithoutPayment().includes(appointment.service_name) && (
                      <span className="text-xs text-orange-600">
                        טרם שולם
                      </span>
                    )}
                    {(appointment.is_paid === true || appointment.is_paid === 1) && !Service.getServicesWithoutPayment().includes(appointment.service_name) && (
                      <span className="text-xs text-green-600">
                        שולם
                      </span>
                    )}
                  </div>
                  {showCompleteButton && (
                    <button
                      onClick={(e) => handleComplete(e, appointment)}
                      disabled={isCompleting}
                      className={`p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200 flex items-center justify-center group-hover:shadow-md ${
                        isCompleting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="סמן כהושלם"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {showMarkAsPaidButton && (
                    <button
                      onClick={(e) => handleMarkAsPaidClick(e, appointment)}
                      disabled={isMarkingAsPaid}
                      className={`px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors duration-200 flex items-center gap-1 text-xs font-medium group-hover:shadow-md ${
                        isMarkingAsPaid ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="סמן כשולם"
                    >
                      <Check className="w-3 h-3" />
                      סמן כשולם
                    </button>
                  )}
                </div>
              </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="lg:col-span-2 space-y-6">
      {/* Overdue Appointments Section */}
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl">
        {/* Header inside card */}
        <div className="flex justify-between items-center p-6 pb-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 ml-2 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 8h12v8H4V8z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-lg font-bold text-gray-900">פגישות שהסתיימו וטרם עודכנו</span>
            {!loading && overdueAppointments.length > 0 && (
              <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                {overdueAppointments.length}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 bg-red-50/50 rounded-xl">
              <p className="text-red-600 mb-2">שגיאה בטעינת הלו"ז</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          ) : (
            renderAppointmentList(overdueAppointments, 'כל הפגישות מעודכנות', true)
          )}
        </div>
      </div>

      {/* Unpaid Appointments Section */}
      <div className="bg-white/70 backdrop-blur-lg rounded-2xl border border-white/20 shadow-xl">
        {/* Header inside card */}
        <div className="flex justify-between items-center p-6 pb-4">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 ml-2 bg-orange-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-orange-600" />
            </div>
            <span className="text-lg font-bold text-gray-900">פגישות שטרם שולמו</span>
            {!loading && unpaidList.length > 0 && (
              <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                {unpaidList.length}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {loading ? (
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 bg-red-50/50 rounded-xl">
              <p className="text-red-600 mb-2">שגיאה בטעינת הלו"ז</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          ) : (
            renderAppointmentList(unpaidList, 'כל הפגישות שולמו', false, true)
          )}
        </div>
      </div>
    </div>
  );
}